<template>
  <div class="assets">
    <v-row class="pl-3">
      <v-col cols="3">
        <v-autocomplete
          v-model="selectedOrg"
          :items="orgName"
          label="Organisations"
          item-text="name"
          outlined
          @change="fetchOrgNodes"
        />
      </v-col>
      <v-col cols="3">
        <v-autocomplete
          v-model="selectedGroup"
          :items="groupsWithId"
          item-text="name"
          item-value="id"
          label="Group"
          outlined
        />
      </v-col>
      <v-col cols="3" class="pt-8">
        <v-text-field
          v-model="search"
          placeholder="Search"
          append-icon="mdi-magnify"
          single-line
          hide-details
        ></v-text-field>
      </v-col>

      <v-spacer />
      <v-col cols="1"></v-col>
      <v-col cols="2" class="d-flex justify-center">
        <v-icon class="pr-6 refresh" @click.native="getAssetsByOrg(paginationInfo.pageNum)">
          mdi-refresh
        </v-icon>
        <v-icon class="download" @click.native="exportExcel">
          mdi-download
        </v-icon>
      </v-col>

      <v-col cols="1" class="d-flex justify-center"> </v-col>
    </v-row>

    <div class="assets-page-container">
      <v-data-table
        v-model="selectedAssets"
        :headers="headers"
        :items="assets"
        :loading="loading"
        :search="search"
        hide-default-footer
        :items-per-page="-1"
        show-select
        item-key="asset_id"
        class="elevation-1 admin-table hardware-faults-report"
      >
        <template v-slot:item.last_seen="{ item }">
          {{ formatDate(readValueFromPresenceMap(item, "last_seen")) }}
        </template>

        <template v-slot:item.location="{ item }">
          {{ readValueFromPresenceMap(item, "location") }}
        </template>

        <template v-slot:item.battery_level="{ item }">
          {{ readValueFromPresenceMap(item, "battery_level") }}
        </template>

        <template v-slot:item.notes="{ item }">
          <v-layout justify-center>
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on">
                  mdi-file
                </v-icon>
              </template>
              <div class="asset-notes">
                <h6 class="notes-title">Notes: {{ item.vehicle_name }}</h6>
                <div v-if="item.notes != ''" class="notes-body">
                  {{ item.notes }}
                </div>
                <div v-else class="notes-body">
                  N/A
                </div>
              </div>
            </v-tooltip>
          </v-layout>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="reports-action-launch" @click="launchRowAction">
            <v-icon>mdi-dots-vertical</v-icon>
            <ActionContainer
              :ref="item.key"
              :heading="'Action'"
              class="report-action-options"
            >
              <ActionItem
                
                label="Edit asset"
                icon="mdi-pencil"
                :disabled="allowOnlySingleAsset || !$can('updateAsset')"
                @click.native="showDialogAddCurrent(item, 'edit')"
              />

              <ActionItem
                :disabled="allowOnlySingleAsset || !$can('viewAsset')"
                label="Device Information"
                icon="mdi-chip"
                @click.native="showDialogAddCurrent(item, 'device_info')"
              />

              <ActionItem
                label="Move to Org"
                :disabled="!$can('moveAsset')"
                icon="mdi-arrow-right"
                @click.native="showDialogAddCurrent(item, 'move')"
              />
              <ActionItem
                
                :disabled="allowOnlySingleAsset || !$can('viewAsset')"
                label="Show Location"
                icon="mdi-map-marker"
                @click.native="showDialogAddCurrent(item, 'location')"
              />
              <ActionItem 
                label="Delete Asset"
                :disabled="!$can('deleteAsset')"
                icon="mdi-delete"
                @click.native="showDialogAddCurrent(item, 'delete')"
              />
            </ActionContainer>
          </div>
        </template>

        <!-- <template v-slot:footer>
          <Pagination
            :items="assets.length"
            @updated="
              (val) => {
                page = val.page;
                itemsPerPage = val.itemsPerPage;
              }
            "
          />
        </template> -->
      </v-data-table>

      <Pagination
        :pagination-info="paginationInfo"
        :show-row-option="true"
        @updatedPage="getAssetsByOrg"
      />
    </div>

    <Dialog
      v-if="showEditAsset && selectedAssets.length > 0"
      :display="showEditAsset"
      :title="'Edit Asset'"
      :state="false"
      :res="{}"
      @close="toggleEditAsset"
    >
      <div class="pa-2">
        <v-row class="pb-0">
          <v-col cols="4" class="pb-0 pt-0">
            Name
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-text-field v-model.trim="editAssetPayloadObj.name" />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-col cols="4" class="pb-0 pt-0">
            Type
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-select
              v-model="editAssetPayloadObj.asset_type"
              :items="assetTypes"
            />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-col cols="4" class="pb-0 pt-0">
            Make
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-text-field v-model.trim="editAssetPayloadObj.make" />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-col cols="4" class="pb-0 pt-0">
            Model
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-text-field v-model.trim="editAssetPayloadObj.model" />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-col cols="4" class="pb-0 pt-0">
            Year
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-text-field v-model.trim="editAssetPayloadObj.year" />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-col cols="4" class="pb-0 pt-0">
            Group
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-select
              v-model="editAssetPayloadObj.node_id"
              :items="groupsWithId"
              item-value="id"
              item-text="name"
            />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-col cols="4" class="pb-0 pt-0">
            Notes
          </v-col>
          <v-col cols="8" class="pb-0 pt-0">
            <v-textarea v-model.trim="editAssetPayloadObj.notes" />
          </v-col>
        </v-row>

        <v-row class="pb-0 pt-0">
          <v-spacer></v-spacer>
          <v-col cols="2">
            <v-btn text color="primary" @click="toggleEditAsset">cancel</v-btn>
          </v-col>
          <v-col cols="2">
            <v-btn
              text
              color="primary"
              :loading="updatingAsset"
              :disabled="disableEditAsset"
              @click.native="updateOrgAssets"
              >Submit</v-btn
            >
          </v-col>
        </v-row>
      </div>
    </Dialog>

    <Dialog
      v-if="firstAssetInSelection"
      :display="showDeviceInfo"
      :title="'Device Information'"
      :state="false"
      :width="500"
      :res="{}"
      @close="toggleDeviceInfo"
    >
      <div class="pa-2">
        <v-row class="pb-1 pt-1 grey-bg pr-0">
          <v-col cols="6" class="pb-0 pt-0">
            Serial Number
          </v-col>
          <v-col cols="4" class="pb-0 pt-0 device-label">
            {{ firstAssetInSelection.serial_number }}
          </v-col>
        </v-row>

        <v-row class="pb-1 pt-1 pr-0">
          <v-col cols="6" class="pb-0 pt-0">
            IMEI
          </v-col>
          <v-col cols="4" class="pb-0 pt-0 device-label">
            {{ firstAssetInSelection.imei }}
          </v-col>
        </v-row>

        <v-row class="pb-1 pt-1 grey-bg pr-0">
          <v-col cols="6" class="pb-0 pt-0">
            SIM ICCID
          </v-col>
          <v-col cols="4" class="pb-0 pt-0 device-label">
            {{ firstAssetInSelection.iccid }}
          </v-col>
        </v-row>

        <v-row class="pb-1 pt-1 pr-0">
          <v-col cols="6" class="pb-0 pt-0">
            Model
          </v-col>
          <v-col cols="4" class="pb-0 pt-0 device-label">
            {{ firstAssetInSelection.device_model }}
          </v-col>
        </v-row>

        <v-row class="pb-1 pt-1 grey-bg pr-0">
          <v-col cols="6" class="pb-0 pt-0">
            Firmware Version
          </v-col>
          <v-col cols="4" class="pb-0 pt-0 device-label">
            {{ firstAssetInSelection.firmware_version }}
          </v-col>
        </v-row>
      </div>
      
    </Dialog>

    <MoveAssetsBetweenOrgs
      v-if="displayMoveAsset"
      :orgs="orgs"
      :asset-list="selectedAssets"
      @close="displayMoveAsset = false"
    />

    <Dialog
      :display="showDeleteAsset"
      :title="'Delete Asset'"
      :state="false"
      :res="{}"
      @close="showDeleteAsset = false"
    >
      <v-row>
        <v-col cols="3" class="text-center pt-6">
          <v-icon large color="orange">
            mdi-alert
          </v-icon>
        </v-col>
        <v-col v-if="selectedAssets.length == 1" cols="9">
          The asset will be deleted and its associated device returned to the
          unassigned device pool. Are you sure you want to proceed?
        </v-col>
        <v-col v-else cols="9">
          {{ selectedAssets.length }} assets will be deleted and their
          associated device returned to the unassigned device pool. Are you sure
          you want to proceed?
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="2">
          <v-btn text @click="showDeleteAsset = false">
            no
          </v-btn>
        </v-col>
        <v-spacer />
        <v-col cols="2">
          <v-btn text :loading="deletingAsset" @click="deleteAsset">
            yes
          </v-btn>
        </v-col>
      </v-row>
    </Dialog>

    <Dialog
      :display="showAssetLocation"
      :title="assetLocationTitle"
      :state="false"
      :width="900"
      :res="{}"
      @close="showAssetLocation = false"
    >
      <div v-if="assetLocation && showAssetLocation" class="asset-map">
        <pvcam-wc-map-simple v-if="assetLocation && showAssetLocation" width="100%" height="100%"></pvcam-wc-map-simple>
      </div>
    </Dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

import Dialog from "@/components/general/Dialog.vue";
import MoveAssetsBetweenOrgs from "@/components/general/MoveAssetsBetweenOrgs";
import ActionContainer from "@/components/general/ActionContainer.vue";
import ActionItem from "@/components/general/ActionItem.vue";
import Pagination from "@/components/general/Pagination.vue";

import {
  GetOrgTree,
  getOrgAssetsByNode,
  getOrgAssets,
  getOrgAssetsPresense,
  updateOrgAssets,
  deleteAssets,
} from "@/services/API.js";
import { getCoor } from "@/utils";

const { CameraMatics } = window;

import moment from "moment";
import * as xlsx from "xlsx";

import { Logger } from "aws-amplify";
const logger = new Logger("Asset Page");

export default {
  name: "Assets",

  components: {
    Dialog,
    MoveAssetsBetweenOrgs,
    ActionContainer,
    ActionItem,
    Pagination
  },

  data() {
    return {
      selectedOrg: "",
      selectedGroup: "",
      nodes: [],
      groups: [],
      groupsWithId: [],
      assets: [],
      assetPresence: {},
      selectedAssets: [],
      loading: false,
      search: "",
      page: 1,
      totalPages: 1,
      itemsPerPage: 50,

      headers: [
        {
          text: "Name",
          value: "asset_name",
          align: "left",
        },
        {
          text: "Type",
          value: "asset_type",
          align: "left",
          width: '8%'
        },
        {
          text: "Make",
          value: "asset_make",
          align: "left",
        },
        {
          text: "Model",
          value: "asset_model",
          align: "left",
        },
        {
          text: "Year",
          value: "asset_year",
          align: "left",
          width: '8%'
        },
        {
          text: "Group",
          value: "node_name",
          align: "left",
        },
        {
          text: "Last Seen",
          value: "last_seen",
          align: "left",
        },
        {
          text: "Address",
          value: "location",
          align: "left",
        },
        {
          text: "Battery",
          value: "battery_level",
          align: "left",
        },
        {
          text: "Notes",
          value: "notes",
          align: "left",
        },
        {
          text: "Actions",
          value: "actions",
          align: "left",
        },
      ],

      editAssetPayloadObj: {
        name: "",
        asset_id: "",
        asset_type: "",
        make: "",
        model: "",
        year: "",
        node_id: "",
        notes: "",
      },
      editAssetPayloadList: [],
      showEditAsset: false,
      updatingAsset: false,
      assetTypes: ["Trailer"],

      showDeviceInfo: false,

      displayMoveAsset: false,

      showDeleteAsset: false,
      deletingAsset: false,

      showAssetLocation: false,

      paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        rowsPerPage: 10
      },
    };
  },

  computed: {
    ...mapState(["orgs"]),

    disableEditAsset() {
      return this.editAssetPayloadObj.name == '' ? true : false
    },

    orgName() {
      const orgs = this.orgs.map((org) => ({
        name: org.name,
      }));

      return orgs;
    },

    orgId() {
      return this.retrunOrgID(this.selectedOrg);
    },

    assetsByGroup() {
      if (this.selectedGroup == this.groups[0]) return this.assets;
      return this.assets.filter(
        (asset) => asset.node_name == this.selectedGroup
      );
    },

    firstAssetInSelection() {
      return this.selectedAssets.length > 0
        ? this.selectedAssets[0].devices[0]
        : null;
    },

    lastSelectedAsset() {
      if (!this.selectedAssets) return;
      const total = this.selectedAssets.length;

      return this.selectedAssets.length > 0
        ? this.selectedAssets[total - 1]
        : null;
    },

    assetLocation() {
      if (!this.lastSelectedAsset) return;
      if (this.lastSelectedAsset.length <= 0 || !this.lastSelectedAsset)
        return false;

      if (Object.hasOwn(this.assetPresence, this.lastSelectedAsset.asset_id)) {
        return this.assetPresence[this.lastSelectedAsset.asset_id]["coords"];
      } else {
        return false;
      }
    },

    assetLocationTitle() {
      return this.assetLocation ? `Asset Location: ${this.lastSelectedAsset.asset_name}, ${this.formatDate(this.readValueFromPresenceMap(this.lastSelectedAsset, 'last_seen'))}` : ''
    },

    allowOnlySingleAsset() {
      return this.selectedAssets.length > 1 ? true : false;
    },

    selectedAssetsIds() {
      return this.selectedAssets.map((asset) => asset.asset_id);
    },
  },

  watch: {
    // selectedOrg() {
      
    // },

    selectedGroup() {
      this.getAssetsByOrg();
    },

    showAssetLocation(val) {
      if(val) {
        setTimeout(() => {
          this.showLocation(this.assetLocation)
        }, 750)
      }
      if(!val) {
        this.selectedAssets = []
      }
    },

    showEditAsset(val) {
      if(!val) {
        this.selectedAssets = []
      }
    },

    showDeviceInfo(val) {
      if(!val) {
        this.selectedAssets = []
      }
    }
  },

  mounted() {
    this.getOrgs();
  },

  methods: {
    ...mapActions(["getOrgs"]),

    getDeviceInfo(item, attribute) {
      if(!item || !attribute) return
      if(!Object.hasOwn(item, 'devices')) return
      
      let device = item.devices.find(device => device.is_defaultdevice == 'true' || device.is_defaultdevice == true)

      return device ? device[attribute] : ''
    },

    showLocation (location) {

      if(location) {
        try {
          window.EventBus.$emit("showEvent", {
            coor: location,
            icon: 'trailer',
            content: false
          })
        } 
        
        catch (error) {
            logger.debug("event error", error)
        }
      }
    },

    findNodeNameById(id) {
      const target = this.groupsWithId.find((node) => node.id == id);

      if (target) {
        return target.name;
      }
    },

    async deleteAsset() {
      this.deletingAsset = true;

      try {
        const deleteAsset = await deleteAssets(
          this.orgId,
          this.selectedAssetsIds
        );

        if (deleteAsset && Object.hasOwn(deleteAsset, "success") && deleteAsset.success.length > 0) {
          this.deletingAsset = false;
          this.showDeleteAsset = false
          this.selectedAssets = []
          this.getAssetsByOrg();
        } else {
          throw new Error("A problem occured deleting the asset(s)");
        }
      } catch {
        this.deletingAsset = false;
      }
    },

    async updateOrgAssets() {
      this.updatingAsset = true;
      try {
        const update = await updateOrgAssets([this.editAssetPayloadObj]);

        if (update && Object.hasOwn(update, "assets")) {

          this.assets = this.assets.map((asset) =>
          asset.asset_id == this.editAssetPayloadObj.asset_id
            ? {
                ...asset,
                asset_name: this.editAssetPayloadObj.name,
                asset_type: this.editAssetPayloadObj.asset_type,
                asset_make: this.editAssetPayloadObj.make,
                asset_model: this.editAssetPayloadObj.model,
                asset_year: this.editAssetPayloadObj.year,
                node_id: this.editAssetPayloadObj.node_id,
                notes: this.editAssetPayloadObj.notes,
                node_name: this.findNodeNameById(this.editAssetPayloadObj.node_id),
              }
            : asset
          );
            
          this.updatingAsset = false;
          this.showEditAsset = false
          // this.getAssetsByOrg(true);
        } else {
          throw new Error("A problem occured updating the asset(s)");
        }
      } catch {
        this.updatingAsset = false;
      }
    },

    async fetchOrgNodes() {
      this.selectedGroup = "";
      const orgTree = await GetOrgTree(this.orgId);

      if (orgTree) {
        this.nodes = orgTree;

        this.groups = this.flattenOrgTree([this.nodes.root_node]);
        this.groupsWithId = this.flattenOrgTreeToObject([this.nodes.root_node])
        this.selectedGroup = this.groupsWithId[0].id;
      }
    },

    retrunOrgID(name) {
      let org = this.orgs.find((org) => {
        return org.name == name;
      });

      return org ? org.id : "";
    },

    flattenOrgTree(node) {
      var result = [];

      node.forEach((a) => {
        result.push(a.metadata.label);
        if (Array.isArray(a.nodes)) {
          result = result.concat(this.flattenOrgTree(a.nodes));
        }
      });
      return result;
    },

    flattenOrgTreeToObject(node) {
      var result = [];

      node.forEach((a) => {
        result.push({
          name: a.metadata.label,
          id: a.id
        });
        if (Array.isArray(a.nodes)) {
          result = result.concat(this.flattenOrgTreeToObject(a.nodes));
        }
      });
      return result;
    },

    async getAssetsByOrg(page = 1) {
      this.loading = true;
      this.assets = [];

      await Promise.all([this.getOrgAssetsPresense(), this.getOrgAssets(page)])
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    async getOrgAssetsPresense() {
      const presence = await getOrgAssetsPresense(this.orgId);
      const addLocations = await this.findLocations(presence);

      this.assetPresence = addLocations.reduce((acc, obj) => {
        acc[obj.asset_id] = obj;
        return acc;
      }, {});
    },

    async getOrgAssets(page) {
      this.assets = []
      this.selectedAssets = []
      
      const assets = await getOrgAssetsByNode(this.orgId, page, this.selectedGroup);

      if (assets && Object.hasOwn(assets, "Assets")) {
        this.assets = assets.Assets;
        this.totalPages = assets.total_pages;
        this.paginationInfo.pageNum = assets.page_num;
        this.paginationInfo.pageCount = assets.total_pages;
        
        // We need to find the closest option of rows per page from the set list of pagination page size select option will break :(
        const rowOptions = [
          20,
          50,
          100,
          200
        ]
        const targetPageSize = assets.page_size
        const output = rowOptions.reduce((prev, curr) => Math.abs(curr - targetPageSize) < Math.abs(prev - targetPageSize) ? curr : prev);

        this.paginationInfo.rowsPerPage = output
      }
    },

    readValueFromPresenceMap(item, val) {
      if (!item || !val) return "";

      if (!Object.hasOwn(this.assetPresence, item.asset_id)) return "";

      return this.assetPresence[item.asset_id][val];
    },

    formatDate(val) {
      if (!val) return "";
      return moment(val).format("DD MMM YYYY HH:mm:ss");
    },

    findLocations(dataSet, propetyName = "location") {
      return new Promise(async (res, rej) => {
        try {
          let coords = dataSet.map((i, index) => {
            i.arrayPos = index;
            i.coords = {
              lat: getCoor(i.last_location.coords.lat),
              lng: getCoor(i.last_location.coords.long),
            };
            return i;
          });

          // we want no run all of these requests async so we map each request to a promise
          const promises = coords.map(
            (i) =>
              new Promise(async (res) => {
                try {
                  const location = await CameraMatics.HereMapService.multiRevGeocode(
                    [i.coords]
                  );
                  // if we can find the location
                  if (location.Response) {
                    coords[i.arrayPos][propetyName] =
                      location.Response.Item[0].Result[0].Location.Address.Label;
                    res(true);
                    // if we can't find the location
                  } else {
                    coords[i.arrayPos][propetyName] = "Unknown";
                    res(true);
                  }
                  // if something goes wrong, we just set the location as not known!
                } catch (exception) {
                  coords[i.arrayPos][propetyName] = "Unknown";
                  res(true);
                }
              })
          );

          // wait for all requests to finish the return the ammended set
          await Promise.all(promises);
          res(coords);
        } catch (ex) {
          logger.debug("error finding locations", ex);
          rej(false);
        }
      });
    },

    launchRowAction(e) {
      let allOptions = document.querySelectorAll(".report-action-options");

      for (let option of allOptions) {
        option.classList.remove("active-options");
        if (option === e.target.nextSibling) {
          option.classList.toggle("active-options");
        }
      }
    },

    showDialogAddCurrent(item, type) {
      let selectedAmount = this.selectedAssets.length;

      if (item) {
        if (type == "edit" || type == "device_info" || type == "location") {
          this.selectedAssets = [item]
        }
        if (!this.selectedAssets.includes(item)) {
          this.selectedAssets.push(item);
        }
      } else {
        // Clear selection for single select
        if (selectedAmount === 1) {
          this.selectedAssets = [];
        }
      }

      if (type == "edit") {
        this.toggleEditAsset();
      }

      if (type == "device_info") {
        this.toggleDeviceInfo();
      }

      if (type == "move") {
        this.displayMoveAsset = true;
      }
      if (type == "unassign") {
        this.$emit("unassignAssetDevice");
      }
      if (type == "location") {
        this.showAssetLocation = true;
      }
      if (type == "delete") {
        this.showDeleteAsset = true;
      }
    },

    toggleEditAsset() {
      this.showUpdateError = false;
    
      let masterAsset = {};

      if (this.selectedAssets.length > 0) {
        masterAsset = this.selectedAssets[0];

        this.editAssetPayloadObj.name = masterAsset.asset_name;
        this.editAssetPayloadObj.asset_id = masterAsset.asset_id;
        this.editAssetPayloadObj.asset_type = masterAsset.asset_type;
        this.editAssetPayloadObj.make = masterAsset.asset_make;
        this.editAssetPayloadObj.model = masterAsset.asset_model;
        this.editAssetPayloadObj.year = masterAsset.asset_year;
        this.editAssetPayloadObj.node_id = masterAsset.node;
        this.editAssetPayloadObj.notes = masterAsset.notes;
      }

      this.showEditAsset = !this.showEditAsset;
    },

    toggleDeviceInfo() {
      this.showDeviceInfo = !this.showDeviceInfo;
    },

    async getAllOrgAssets() {
      const allResults = [];
      let currentPage = 1;

      for (let i = 0; i < this.totalPages; i++) {
        try {
          currentPage = i + 1;

          const assets = await getOrgAssets(
            this.orgId,
            currentPage,
            this.itemsPerPage
          );

          if (!assets) {
            throw new Error(`Request failed`);
          }
          const data = await assets.Assets;

          if (Array.isArray(data)) {
            allResults.push(...data);
          } else {
            throw new Error(`Result is not array ${assets.Assets}`);
          }
        } catch (error) {
          throw new Error(`Error during API call ${i + 1}:`, error);
        }
      }

      return allResults;
    },

    async exportExcel() {
      this.excelLoading = true;
      const exportSheetName = `${this.selectedOrg}_assets_${moment().format(
        "YYYY-MM-DDTHH:mm:ss"
      )}`;

      const headers = [
        "Name",
        "Type",
        "Make",
        "Model",
        "Year",
        "Group",
        "Last Seen",
        "Address",
        "Battery Level (mv)",
        "Notes",
      ];
      const allAssets = await this.getAllOrgAssets();

      const assets = allAssets.map((item) => ({
        ...item,
        last_seen: this.readValueFromPresenceMap(item, "last_seen"),
        location: this.readValueFromPresenceMap(item, "location"),
        battery_level: this.readValueFromPresenceMap(item, "battery_level"),
      }));

      const data = [
        headers,
        [],
        ...assets.map((item) => [
          item.asset_name,
          item.asset_type,
          item.asset_make,
          item.asset_model,
          item.asset_year,
          item.node_name,
          item.last_seen,
          item.location,
          item.battery_level,
          item.notes,
        ]),
      ];

      // Create a new workbook
      const wb = xlsx.utils.book_new();

      const ws = xlsx.utils.aoa_to_sheet(data);

      const colWidths = data[0].map((_, colIndex) => {
        const maxLength = data.reduce((max, row) => {
          const cellValue =
            row && row[colIndex] ? row[colIndex].toString() : "";
          return Math.max(max, cellValue.length);
        }, 0);
        return { width: maxLength + 2 }; // Add a little extra padding
      });

      // Set column widths in the worksheet
      ws["!cols"] = colWidths;

      // Append the worksheet to the workbook
      xlsx.utils.book_append_sheet(wb, ws, "Assets");

      // Write the workbook to a file (e.g., 'example.xlsx')
      xlsx.writeFile(wb, exportSheetName + ".xlsx");
      this.excelLoading = false;
    },
  },
};
</script>

<style lang="scss">
.reports-action-launch {
  position: relative;
  cursor: pointer;
}
.report-action-options {
  display: none;
}
.active-options {
  display: block !important;
}
.grey-bg {
  background: rgba(0, 0, 0, 0.05);
}
.error-message {
  color: #f44336;
  text-align: center;
  width: 100%;
}
.device-label {
  color: rgb(14, 118, 191);
}
.asset-notes {
  width: 200px;
}
.asset-map {
  position: relative;
  height: 500px;
  width: 100%;
}
.assets {
  padding: 1em;
}

.v-data-table__wrapper {
  overflow-x: visible !important;
  overflow-y: visible !important;
}

.refresh {
  &:hover {
    cursor: pointer;
  }
}.download {
  &:hover {
    cursor: pointer;
  }
}
</style>
